import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { combineAll } from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	catchError,
	combineLatest,
	filter,
	map,
	of,
	switchMap,
	tap,
	withLatestFrom,
} from 'rxjs';

import { NgCommService } from '../../ajs-ng-communication.service';
import { AjsScopeService } from '../../ajs-scope.service';
import { AjsStateService } from '../../ajs-state.service';
import { states } from '../../core/application.states';
import { ConnectorRequester } from '../../requesters/connector/connector.requester';
import {
	ConnectorAction,
	ConnectorActionPopupComponent,
	IConnectorActionModalData,
} from '../components/connector-action-popup/connector-action-popup.component';
import { ConnectorItemEntity } from '../components/connector-list/connector-item.entity';
import { ConnectorAdapter } from '../connector.adapter';
import {
	bulkDeleteConnectors,
	deleteConnector,
	deleteConnectorFail,
	fetchConnectorListFail,
	fetchConnectorListSuccess,
	fetchPublicProjectDetails,
	fetchPublicProjectDetailsFail,
	fetchPublicProjectDetailsSuccess,
	initConnectorList,
	navigateToConnector,
	navigateToConnectorCreate,
	navigateToConnectorDuplicate,
	navigateToDepotFromConnector,
	openDeleteConfirmationPopup,
	refreshConnectorList,
	toggleConnectorStatus,
	updateIncludePublic,
} from './connector.actions';
import { ConnectorSelector } from './connector.selector';

@Injectable()
export class ConnectorEffects {
	// ##########################################################
	// Navigation Effects
	// ##########################################################

	public navigateToConnectorCreate = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToConnectorCreate),
				tap(() => this.stateService.go(states.ConnectorCreate))
			),
		{
			dispatch: false,
		}
	);

	public navigateToConnector = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToConnector),
				tap((a) =>
					this.stateService.go(states.ConnectorEdit, {
						id: `${a.id}`,
						type: `${a.connectorType}`,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	public navigateToConnectorDuplicate = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToConnectorDuplicate),
				tap((a) =>
					this.stateService.go(states.ConnectorDuplicate, {
						duplicatedId: `${a.id}`,
						type: a.connectorType,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	public navigateToDepotWithConnector = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToDepotFromConnector),
				tap((a) =>
					this.stateService.go(states.DepotFromConnector, {
						connectorId: `${a.id}`,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	// ##########################################################
	// Business Effects
	// ##########################################################

	public initConnectorList$ = createEffect(() =>
		this.action$.pipe(
			ofType(initConnectorList),
			withLatestFrom(this.connectorSelector.includePublic$()),
			switchMap(([, includePublic]) =>
				this.connectorRequester.getConnectorList(includePublic, true)
			),
			map((response) =>
				fetchConnectorListSuccess({
					connectorList: this.connectorAdapter.generateConnectorList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchConnectorListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshConnectorList$ = createEffect(() =>
		this.action$.pipe(
			ofType(refreshConnectorList),
			withLatestFrom(this.connectorSelector.includePublic$()),
			switchMap(([, includePublic]) =>
				this.connectorRequester.getConnectorList(includePublic, false)
			),
			map((response) =>
				fetchConnectorListSuccess({
					connectorList: this.connectorAdapter.generateConnectorList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchConnectorListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public updateGetPublic$ = createEffect(() =>
		this.action$.pipe(
			ofType(updateIncludePublic),
			map(() => refreshConnectorList())
		)
	);

	public deleteConnectors$ = createEffect(() =>
		this.action$.pipe(
			ofType(deleteConnector),
			withLatestFrom(this.connectorSelector.getConnectorList$()),
			switchMap(([action, list]) =>
				this.connectorRequester.deleteConnector(action.connectorId)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@connector.delete.success:`
				);
				return refreshConnectorList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@connector.delete.error:`
				);
				this.store.dispatch(deleteConnectorFail());
				return caught;
			})
		)
	);

	public bulkDeleteConnectors$ = createEffect(() =>
		this.action$.pipe(
			ofType(bulkDeleteConnectors),
			switchMap((action) =>
				this.connectorRequester.deleteMany(action.connectorIds)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@connector.delete.multiple.success:`
				);
				return refreshConnectorList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@connector.delete.multiple.error:`
				);
				this.store.dispatch(refreshConnectorList());
				return caught;
			})
		)
	);

	public toggleActivationStatus$ = createEffect(() =>
		this.action$.pipe(
			ofType(toggleConnectorStatus),
			switchMap((action) =>
				combineLatest([
					of(action.connectorType),
					this.connectorRequester.getConnector(action.connectorId),
				])
			),
			switchMap(([type, connector]) =>
				this.connectorRequester.updateConnector({
					...connector,
					metadata: {
						...connector.metadata,
						actif: !connector.metadata?.actif,
					},
					connector_type: type,
				})
			),
			map(() => refreshConnectorList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(refreshConnectorList());
				return caught;
			})
		)
	);

	public publicProjectDetails$ = createEffect(() =>
		this.action$.pipe(
			ofType(fetchPublicProjectDetails),
			switchMap((action) =>
				combineAll({
					connectorId: action.connectorId,
				})
			),
			map(({ connectorId }) =>
				fetchPublicProjectDetailsSuccess({
					connectorId,
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchPublicProjectDetailsFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	// ##########################################################
	// Confirmation popup effects
	// ##########################################################

	public openDeleteConfirmationPopup$ = createEffect(() =>
		this.action$.pipe(
			ofType(openDeleteConfirmationPopup),
			switchMap((action) => {
				const modalRef = this.modalService.openPopup<IConnectorActionModalData>(
					ConnectorActionPopupComponent,
					{
						title: $localize`:i18n=@@connector.action.popup.delete.header:`,
						subTitle: '',
						connectorId: action.connectorId,
						element: action.connectorLabel,
						action: ConnectorAction.Delete,
					},
					{
						disableClose: true,
					}
				);
				return combineLatest([of(action), modalRef.afterClosed()]);
			}),
			filter(([, modalResult]) => modalResult !== undefined),
			map(([action]) => {
				if (action.connectorId.length === 1) {
					return deleteConnector({
						connectorId: action.connectorId[0],
					});
				} else {
					return bulkDeleteConnectors({
						connectorIds: action.connectorId,
					});
				}
			})
		)
	);
	public constructor(
		private readonly action$: Actions,
		private readonly connectorSelector: ConnectorSelector,
		private readonly connectorRequester: ConnectorRequester,
		private readonly connectorAdapter: ConnectorAdapter,
		private readonly stateService: AjsStateService,
		private readonly store: Store,
		private readonly modalService: AppModalService,
		private readonly ngCommService: NgCommService,
		private readonly ajsScopeService: AjsScopeService
	) {}
}
